import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { buildBackendUrl } from '@/lib/backend-url'
import { actions } from '@/store'

const MIN_AUTOCOMPLETE_LENGTH = 2
const AUTOCOMPLETE_DEBOUNCE_MS = 220
const MAX_COMPLETION_LENGTH = 140
const MAX_IGNORED_COMPLETION_REPEATS = 2

const AUTOCOMPLETE_ENDPOINT = buildBackendUrl('/autocomplete')

const normalizeCompletion = (inputText, rawCompletion) => {
  if (typeof rawCompletion !== 'string') return ''

  // Standardize whitespace but preserve single leading space if AI provided it intentionally
  let completion = rawCompletion.replace(/\r?\n+/g, ' ').replace(/\s{2,}/g, ' ')
  if (!completion.trim()) return ''

  const lowerInput = inputText.toLowerCase()
  const lowerCompletion = completion.toLowerCase()

  // 1. Check for exact suffix overlap (handles partial word completion like "pharm" -> "acy")
  let overlapLength = 0
  const maxPossibleOverlap = Math.min(inputText.length, completion.length)

  for (let i = 1; i <= maxPossibleOverlap; i++) {
    if (lowerInput.endsWith(lowerCompletion.substring(0, i))) {
      overlapLength = i
    }
  }

  // 2. Check for word-level overlap (handles cases where input ends with space e.g., "to buy " -> "to buy supplements")
  if (overlapLength === 0 && inputText.trimEnd().length > 0) {
    const trimmedInput = lowerInput.trimEnd()
    const trimmedCompletion = lowerCompletion.trimStart()

    // Find if the completion starts with a word that the input ends with
    const inputWords = trimmedInput.split(/\s+/)
    const completionWords = trimmedCompletion.split(/\s+/)

    // Check if the last 1 or 2 words of input match the start of completion
    for (let j = Math.min(inputWords.length, 3); j > 0; j--) {
      const inputSuffix = inputWords.slice(-j).join(' ')
      const completionPrefix = completionWords.slice(0, j).join(' ')

      if (inputSuffix === completionPrefix) {
        // We found a word-level overlap.
        // We need to find where this prefix ends in the original (untrimmed) completion
        // to slice it correctly.
        const prefixIndex = lowerCompletion.indexOf(completionPrefix)
        if (prefixIndex !== -1) {
          overlapLength = prefixIndex + completionPrefix.length
          break
        }
      }
    }
  }

  if (overlapLength > 0) {
    completion = completion.slice(overlapLength)
  }

  // If there's no overlap and no space between input and completion, add one
  // unless the completion starts with punctuation
  if (
    completion.length > 0 &&
    inputText.length > 0 &&
    !inputText.endsWith(' ') &&
    !completion.startsWith(' ') &&
    !/^[,.;:!?]/.test(completion)
  ) {
    completion = ' ' + completion
  }

  if (!completion.match(/[a-zA-Z0-9]/)) {
    // If it's just whitespace or punctuation, only return if it adds something meaningful
    if (!completion.trim()) return ''
  }

  return completion.slice(0, MAX_COMPLETION_LENGTH)
}

const mergeCompletion = (currentText, completion) => {
  if (!completion) return currentText

  // If both have a space at the boundary, avoid double spaces
  if (currentText.endsWith(' ') && completion.startsWith(' ')) {
    return `${currentText}${completion.slice(1)}`
  }

  return `${currentText}${completion}`
}

const buildAutocompleteUrl = (text, type = 'task', parentId = '') => {
  const url = new URL(AUTOCOMPLETE_ENDPOINT, window.location.origin)
  url.searchParams.set('q', text)
  if (type) url.searchParams.set('type', type)
  if (parentId) url.searchParams.set('parentId', parentId)
  url.searchParams.set('timezone', Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC')
  return url.toString()
}

const normalizeCompletionKey = (value) =>
  (value || '')
    .trim()
    .toLowerCase()
    .replace(/[^\w\s]/g, '')
    .replace(/\s+/g, ' ')

const shouldRequestAutocomplete = (text, isEnabled) => {
  if (!isEnabled) return false
  if (typeof text !== 'string') return false

  const trimmed = text.trim()
  return trimmed.length >= MIN_AUTOCOMPLETE_LENGTH
}

export function useTaskAutocomplete(text, isEnabled = true, options = {}) {
  const { type = 'task', parentId = '' } = options
  const [completion, setCompletion] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const requestSequenceRef = useRef(0)
  const abortControllerRef = useRef(null)
  const ignoredCompletionCountsRef = useRef(new Map())
  const lastShownSuggestionRef = useRef({ baseText: '', completion: '' })

  const shouldRequest = useMemo(() => shouldRequestAutocomplete(text, isEnabled), [isEnabled, text])

  useEffect(() => {
    const lastShown = lastShownSuggestionRef.current
    if (!lastShown.completion || text === lastShown.baseText) return

    const acceptedText = mergeCompletion(lastShown.baseText, lastShown.completion)
    const suggestionKey = normalizeCompletionKey(lastShown.completion)
    if (text === acceptedText) {
      ignoredCompletionCountsRef.current.delete(suggestionKey)
    } else if (suggestionKey) {
      const nextCount = (ignoredCompletionCountsRef.current.get(suggestionKey) || 0) + 1
      ignoredCompletionCountsRef.current.set(suggestionKey, nextCount)
    }

    lastShownSuggestionRef.current = { baseText: '', completion: '' }
  }, [text])

  useEffect(() => {
    if (!shouldRequest) {
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
      setCompletion('')
      setIsLoading(false)
      return undefined
    }

    const requestId = requestSequenceRef.current + 1
    requestSequenceRef.current = requestId
    setCompletion('')

    const timeoutId = window.setTimeout(async () => {
      const controller = new AbortController()
      abortControllerRef.current?.abort()
      abortControllerRef.current = controller
      setIsLoading(true)

      try {
        const response = await fetch(buildAutocompleteUrl(text, type, parentId), {
          signal: controller.signal,
          headers: {
            Accept: 'text/plain',
          },
          credentials: 'include',
        })

        if (!response.ok) {
          throw new Error(`Autocomplete request failed with ${response.status}`)
        }

        const rawCompletion = await response.text()
        if (requestSequenceRef.current !== requestId || controller.signal.aborted) {
          return
        }

        const normalizedCompletion = normalizeCompletion(text, rawCompletion)
        const suggestionKey = normalizeCompletionKey(normalizedCompletion)
        const ignoredCount = ignoredCompletionCountsRef.current.get(suggestionKey) || 0
        if (!normalizedCompletion || ignoredCount >= MAX_IGNORED_COMPLETION_REPEATS) {
          setCompletion('')
          return
        }

        lastShownSuggestionRef.current = {
          baseText: text,
          completion: normalizedCompletion,
        }
        setCompletion(normalizedCompletion)
      } catch {
        if (!controller.signal.aborted && requestSequenceRef.current === requestId) {
          setCompletion('')
        }
      } finally {
        if (requestSequenceRef.current === requestId) {
          setIsLoading(false)
        }
      }
    }, AUTOCOMPLETE_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
    }
  }, [shouldRequest, text])

  useEffect(
    () => () => {
      abortControllerRef.current?.abort()
      abortControllerRef.current = null
    },
    [],
  )

  const acceptCompletion = useCallback(
    (currentText) => {
      if (!completion) return currentText
      const nextText = mergeCompletion(currentText, completion)
      const suggestionKey = normalizeCompletionKey(completion)
      if (suggestionKey) {
        ignoredCompletionCountsRef.current.delete(suggestionKey)
      }
      lastShownSuggestionRef.current = { baseText: '', completion: '' }
      setCompletion('')
      actions.incrementAIAction()
      return nextText
    },
    [completion],
  )

  return {
    completion,
    isLoading,
    hasCompletion: completion.length > 0,
    acceptCompletion,
  }
}
